import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CreditCard,
  ShieldCheck,
  UserCircle2,
  Mail,
  Sparkles,
  ExternalLink,
  BadgeCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Footer } from '../components/LegalDisclaimers';
import { SecuritySection } from '../components/SecuritySection';
import UserMenu from '../components/UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true,
        });
        setUser(resp.data);
      } catch (e) {
        if (e.response?.status === 401) navigate('/');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Deep-link support: /account?section=security scrolls to security card.
  useEffect(() => {
    const section = searchParams.get('section');
    if (!section || loading) return;
    // Give the DOM a tick after data arrives before scrolling.
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="account-section-${section}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
    return () => clearTimeout(id);
  }, [searchParams, loading]);

  const plan = user?.subscription_plan || 'free';
  const isPaid = plan === 'pro' || plan === 'premium';

  const planLabel =
    plan === 'premium'
      ? t('common.premium')
      : plan === 'pro'
      ? t('common.pro')
      : t('common.free');

  const authProviderLabel = useMemo(() => {
    if (!user?.auth_provider) return t('account.info.authEmail');
    if (user.auth_provider === 'google') return t('account.info.authGoogle');
    if (user.auth_provider === 'both') return t('account.info.authBoth');
    return t('account.info.authEmail');
  }, [user, t]);

  const billingIntervalLabel =
    user?.billing_interval === 'year'
      ? t('account.billing.intervalYearly')
      : user?.billing_interval === 'month'
      ? t('account.billing.intervalMonthly')
      : null;

  const nextBillingDate = useMemo(() => {
    if (!user?.current_period_end) return null;
    try {
      const d = new Date(user.current_period_end);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  }, [user, i18n.language]);

  const cancelPending = Boolean(user?.cancel_at_period_end) && !!nextBillingDate;

  const statusLabel = useMemo(() => {
    const s = user?.subscription_status;
    if (!s) return null;
    const key = `account.billing.status.${s}`;
    const translated = t(key);
    return translated === key ? s : translated;
  }, [user, t]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/subscription/portal`,
        {},
        { withCredentials: true },
      );
      const url = resp?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(t('account.billing.portalError'));
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        toast.error(t('account.billing.noSubscription'));
        navigate('/pricing');
      } else {
        toast.error(t('account.billing.portalError'));
      }
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#45C04A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2">
            <RendPlexLogo size="md" />
          </Link>
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button
              data-testid="account-back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
            </Button>
            <UserMenu user={user} />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-8">
        {/* Header */}
        <div>
          <h1
            data-testid="account-page-title"
            className="font-primary font-extrabold text-3xl lg:text-4xl text-[#12355B] dark:text-white tracking-tight"
          >
            {t('account.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('account.subtitle')}
          </p>
        </div>

        {/* A. Account Information */}
        <section
          data-testid="account-section-info"
          className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
              <UserCircle2 className="w-5 h-5 text-[#45C04A]" />
            </div>
            <h2 className="font-primary font-bold text-xl md:text-2xl text-[#12355B] dark:text-white">
              {t('account.info.title')}
            </h2>
          </div>

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                {t('account.info.name')}
              </dt>
              <dd
                data-testid="account-info-name"
                className="text-[#12355B] dark:text-white font-medium"
              >
                {user?.name || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                {t('account.info.email')}
              </dt>
              <dd
                data-testid="account-info-email"
                className="text-[#12355B] dark:text-white font-medium flex items-center gap-2"
              >
                <Mail className="w-4 h-4 text-slate-400" />
                {user?.email || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                {t('account.info.plan')}
              </dt>
              <dd className="flex items-center gap-2">
                <span
                  data-testid="account-info-plan"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                    plan === 'premium'
                      ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B]'
                      : plan === 'pro'
                      ? 'bg-gradient-to-r from-[#45C04A] to-[#65D95A] text-[#12355B]'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                  }`}
                >
                  <BadgeCheck className="w-3.5 h-3.5" />
                  {planLabel}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                {t('account.info.authMethod')}
              </dt>
              <dd
                data-testid="account-info-auth"
                className="text-[#12355B] dark:text-white font-medium"
              >
                {authProviderLabel}
              </dd>
            </div>
          </dl>
        </section>

        {/* B. Billing & Subscription */}
        <section
          data-testid="account-section-billing"
          className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#45C04A]" />
            </div>
            <h2 className="font-primary font-bold text-xl md:text-2xl text-[#12355B] dark:text-white">
              {t('account.billing.title')}
            </h2>
          </div>

          {isPaid ? (
            <div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                    {t('account.billing.currentPlan')}
                  </dt>
                  <dd
                    data-testid="billing-current-plan"
                    className="text-[#12355B] dark:text-white font-semibold"
                  >
                    {planLabel}
                  </dd>
                </div>
                {billingIntervalLabel && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                      {t('account.billing.interval')}
                    </dt>
                    <dd
                      data-testid="billing-interval"
                      className="text-[#12355B] dark:text-white font-medium"
                    >
                      {billingIntervalLabel}
                    </dd>
                  </div>
                )}
                {statusLabel && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                      {t('account.billing.statusLabel')}
                    </dt>
                    <dd
                      data-testid="billing-status"
                      className="text-[#12355B] dark:text-white font-medium capitalize"
                    >
                      {statusLabel}
                    </dd>
                  </div>
                )}
                {nextBillingDate && !cancelPending && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                      {t('account.billing.nextBilling')}
                    </dt>
                    <dd
                      data-testid="billing-next-date"
                      className="text-[#12355B] dark:text-white font-medium"
                    >
                      {nextBillingDate}
                    </dd>
                  </div>
                )}
              </dl>

              {cancelPending && (
                <div
                  data-testid="billing-cancel-notice"
                  className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 px-4 py-3 mb-6"
                >
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('account.billing.cancelsOn', { date: nextBillingDate })}
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {t('account.billing.portalHelp')}
              </p>

              <Button
                data-testid="billing-manage-subscription-btn"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {portalLoading
                  ? t('account.billing.opening')
                  : t('account.billing.manageBtn')}
              </Button>
            </div>
          ) : (
            <div>
              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-5 mb-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#45C04A] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#12355B] dark:text-white">
                      {t('account.billing.freeTitle')}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t('account.billing.freeBody')}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                data-testid="billing-view-plans-btn"
                onClick={() => navigate('/pricing')}
                className="rounded-xl bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('account.billing.viewPlans')}
              </Button>
            </div>
          )}
        </section>

        {/* C. Security + Active Sessions */}
        <section data-testid="account-section-security">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#45C04A]" />
            </div>
            <h2 className="font-primary font-bold text-xl md:text-2xl text-[#12355B] dark:text-white">
              {t('account.security.title')}
            </h2>
          </div>
          <SecuritySection user={user} />
        </section>
      </div>

      <Footer />
    </div>
  );
}
